import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import dynamic from "next/dynamic";

import { useTranslation } from "next-i18next";

import { loadI18nNamespaces } from "../../../hooks/useLoadI18nNamespaces";
import { WEB_V2_BIDDING_LOCALIZATION_NAMESPACES } from "../../../methods/getRouteLocalizationNamespaces";
import { LoadMarketCard } from "../../../services/load/useGetLoadMarketV2/type";
import { IEventTrackerProp } from "../../../types/events";

const CompactLoadBiddingModal = dynamic(() => import("./CompactLoadBiddingModal"), {
    ssr: false
});

interface OpenCompactBiddingOptions {
    loadData: LoadMarketCard;
    onSuccess?: () => void;
    trackRank?: number;
}

interface CompactLoadBiddingContextValue {
    isCompactBiddingOpen: boolean;
    openCompactBidding: (options: OpenCompactBiddingOptions) => void;
    closeCompactBidding: () => void;
}

const CompactLoadBiddingContext = createContext<CompactLoadBiddingContextValue | null>(null);

export function CompactLoadBiddingProvider({
    children,
    trackEvent,
    eventData
}: React.PropsWithChildren<IEventTrackerProp>) {
    const { i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [loadData, setLoadData] = useState<LoadMarketCard | null>(null);
    const [onSuccess, setOnSuccess] = useState<(() => void) | undefined>();
    const [trackRank, setTrackRank] = useState<number | undefined>();
    const [sessionKey, setSessionKey] = useState("closed");

    const openCompactBidding = useCallback(
        ({ loadData: nextLoad, onSuccess: nextOnSuccess, trackRank: rank }: OpenCompactBiddingOptions) => {
            void loadI18nNamespaces(i18n, WEB_V2_BIDDING_LOCALIZATION_NAMESPACES).then(() => {
                setLoadData(nextLoad);
                setOnSuccess(() => nextOnSuccess);
                setTrackRank(rank);
                setSessionKey(String(nextLoad.load_detail?.id || Date.now()));
                setIsOpen(true);
            });
        },
        [i18n]
    );

    const closeCompactBidding = useCallback(() => {
        setIsOpen(false);
    }, []);

    const value = useMemo(
        () => ({
            isCompactBiddingOpen: isOpen,
            openCompactBidding,
            closeCompactBidding
        }),
        [closeCompactBidding, isOpen, openCompactBidding]
    );

    return (
        <CompactLoadBiddingContext.Provider value={value}>
            {children}
            {isOpen && loadData ? (
                <CompactLoadBiddingModal
                    key={sessionKey}
                    isOpen={isOpen}
                    loadData={loadData}
                    onClose={closeCompactBidding}
                    onSuccess={onSuccess}
                    trackRank={trackRank}
                    trackEvent={trackEvent}
                    eventData={eventData}
                />
            ) : null}
        </CompactLoadBiddingContext.Provider>
    );
}

export function useCompactLoadBidding() {
    const context = useContext(CompactLoadBiddingContext);

    if (!context) {
        throw new Error("useCompactLoadBidding must be used within CompactLoadBiddingProvider");
    }

    return context;
}

/** Non-throwing variant. Returns `null` when no provider is mounted. */
export function useCompactLoadBiddingOptional() {
    return useContext(CompactLoadBiddingContext);
}
